import type {BillingPeriod, PlanInfo, PlanPriceInfo, SubscriptionStatus} from "@/api/billing";

export function hasPaidAccess(status?: SubscriptionStatus | null): boolean {
  return (
    status === "TRIALING" ||
    status === "ACTIVE" ||
    status === "GRACE" ||
    status === "ADMIN_OVERRIDE"
  );
}

const MONTHLY_BILLING_PERIOD = "MONTHLY";
const TRIAL_BILLING_PERIOD = "ONE_TIME";

export interface SubscriptionOffer {
  plan: PlanInfo;
  price: PlanPriceInfo;
  trial: boolean;
}

const BILLING_PERIOD_ORDER: Record<BillingPeriod, number> = {
  ONE_TIME: 0,
  WEEKLY: 1,
  MONTHLY: 2,
  QUARTERLY: 3,
  YEARLY: 4,
};

export function listSubscriptionOffers(
  plans: PlanInfo[] | undefined,
  trialEligible: boolean,
): SubscriptionOffer[] {
  return (plans ?? [])
    .flatMap((plan) => plan.prices.map((price) => ({
      plan,
      price,
      trial: price.billing_period === TRIAL_BILLING_PERIOD && (plan.trial_days ?? 0) > 0,
    })))
    .filter((offer) => offer.price.billing_period !== TRIAL_BILLING_PERIOD || (offer.trial && trialEligible))
    .sort((left, right) => BILLING_PERIOD_ORDER[left.price.billing_period]
      - BILLING_PERIOD_ORDER[right.price.billing_period]);
}

export function resolveMonthlyPrice(
  plans: PlanInfo[] | undefined,
): PlanPriceInfo | undefined {
  return plans?.flatMap((plan) => plan.prices)
    .find((price) => price.billing_period === MONTHLY_BILLING_PERIOD);
}

export function resolveTrialPrice(
  plans: PlanInfo[] | undefined,
): PlanPriceInfo | undefined {
  return plans?.find((plan) => (plan.trial_days ?? 0) > 0)
    ?.prices.find((price) => price.billing_period === TRIAL_BILLING_PERIOD);
}

export function resolveTrialDays(plans: PlanInfo[] | undefined): number {
  return plans?.find((plan) => (plan.trial_days ?? 0) > 0)?.trial_days ?? 3;
}

export function isTrialSubscription(
  subscription:
    | {
        plan?: { slug?: string; trial_days?: number } | null;
        plan_price?: { billing_period?: string } | null;
      }
    | null
    | undefined,
): boolean {
  if (!subscription) {
    return false;
  }
  return subscription.plan_price?.billing_period === TRIAL_BILLING_PERIOD
    && (subscription.plan?.trial_days ?? 0) > 0;
}

export function resolveRenewalPlanPrice(
  subscription:
    | {
        plan?: PlanInfo | null;
        plan_price?: PlanPriceInfo | null;
      }
    | null
    | undefined,
  plans: PlanInfo[] | undefined,
): PlanPriceInfo | undefined {
  if (isTrialSubscription(subscription)) {
    return resolveMonthlyPrice(plans);
  }
  if (subscription?.plan_price?.billing_period
    && subscription.plan_price.billing_period !== TRIAL_BILLING_PERIOD) {
    return subscription.plan_price;
  }
  const planId = subscription?.plan?.id;
  const matched = plans?.find((plan) => plan.id === planId);
  return (
    matched?.prices.find((p) => p.billing_period === MONTHLY_BILLING_PERIOD) ??
    resolveMonthlyPrice(plans) ??
    subscription?.plan_price ??
    undefined
  );
}

export function billingPeriodTitle(period: BillingPeriod): string {
  switch (period) {
    case "WEEKLY":
      return "Неделя";
    case "MONTHLY":
      return "Месяц";
    case "QUARTERLY":
      return "3 месяца";
    case "YEARLY":
      return "Год";
    case "ONE_TIME":
      return "Пробный доступ";
  }
}

export function billingPeriodPriceSuffix(period: BillingPeriod): string {
  switch (period) {
    case "WEEKLY":
      return "за неделю";
    case "MONTHLY":
      return "за месяц";
    case "QUARTERLY":
      return "за 3 месяца";
    case "YEARLY":
      return "за год";
    case "ONE_TIME":
      return "один раз";
  }
}

export function billingPeriodChargeFrequency(period: BillingPeriod): string {
  switch (period) {
    case "WEEKLY":
      return "еженедельно";
    case "MONTHLY":
      return "ежемесячно";
    case "QUARTERLY":
      return "раз в 3 месяца";
    case "YEARLY":
      return "ежегодно";
    case "ONE_TIME":
      return "однократно";
  }
}

export function formatMinor(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  if (currency === "RUB") {
    return `${major.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;
  }
  return `${major.toLocaleString("ru-RU")} ${currency}`;
}

export type PaywallMode = "base" | "referral" | "leader";

export function resolvePaywallMode(
  isLeader: boolean,
  hasReferralBinding: boolean,
): PaywallMode {
  if (isLeader) {
    return "leader";
  }
  if (hasReferralBinding) {
    return "referral";
  }
  return "base";
}

export function resolveChargeAmountMinor(
  price: PlanPriceInfo,
  mode: PaywallMode,
): number {
  if (mode === "leader") {
    return price.leader_amount_minor;
  }
  if (mode === "referral") {
    return price.referral_amount_minor;
  }
  return price.amount_minor;
}

export function formatPlanPriceDisplay(
  price: PlanPriceInfo,
  hasReferralBinding: boolean,
) {
  const mode = resolvePaywallMode(false, hasReferralBinding);
  const checkoutMinor = resolveChargeAmountMinor(price, mode);
  const checkoutLabel = formatMinor(checkoutMinor, price.currency);
  const hasReferralDiscount =
    price.referral_amount_minor < price.amount_minor;
  const baseLabel = hasReferralBinding
    ? hasReferralDiscount
      ? formatMinor(price.amount_minor, price.currency)
      : null
    : hasReferralDiscount
      ? formatMinor(price.referral_amount_minor, price.currency)
      : null;
  return {
    checkoutLabel,
    baseLabel,
    baseNotePrefix: hasReferralBinding ? "Без кода" : "С кодом",
  };
}
